import React, { useState, useRef } from 'react';
import { Upload, X, Star, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { IMAGE_UPLOAD_CONFIG, validateImageFile } from '../../constants/propertyConstants';

/**
 * ImageUploader Component
 * Supports drag & drop, multiple files, preview, and featured image selection
 */
const ImageUploader = ({ images, onImagesChange, maxFiles = IMAGE_UPLOAD_CONFIG.MAX_FILES }) => {
    const [dragActive, setDragActive] = useState(false);
    const [errors, setErrors] = useState([]);
    const fileInputRef = useRef(null);

    /**
     * Handle file selection
     */
    const handleFiles = (files) => {
        const fileArray = Array.from(files);
        const newImages = [];
        const newErrors = [];

        // Check total number of images
        if (images.length + fileArray.length > maxFiles) {
            newErrors.push(`Maximum ${maxFiles} images allowed`);
            setErrors(newErrors);
            return;
        }

        fileArray.forEach((file) => {
            // Validate file
            const validationErrors = validateImageFile(file);

            if (validationErrors.length > 0) {
                newErrors.push(`${file.name}: ${validationErrors.join(', ')}`);
            } else {
                // Create preview URL
                const previewUrl = URL.createObjectURL(file);

                newImages.push({
                    file,
                    previewUrl,
                    title: '',
                    description: '',
                    isFeatured: images.length === 0 && newImages.length === 0 // First image is featured
                });
            }
        });

        if (newErrors.length > 0) {
            setErrors(newErrors);
        } else {
            setErrors([]);
        }

        if (newImages.length > 0) {
            onImagesChange([...images, ...newImages]);
        }
    };

    /**
     * Handle drag events
     */
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    /**
     * Handle drop event
     */
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFiles(e.dataTransfer.files);
        }
    };

    /**
     * Handle file input change
     */
    const handleFileInput = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFiles(e.target.files);
        }
    };

    /**
     * Remove image
     */
    const removeImage = (index) => {
        const newImages = images.filter((_, i) => i !== index);

        // If removed image was featured, make first image featured
        if (images[index].isFeatured && newImages.length > 0) {
            newImages[0].isFeatured = true;
        }

        onImagesChange(newImages);

        // Revoke object URL to free memory
        URL.revokeObjectURL(images[index].previewUrl);
    };

    /**
     * Set featured image
     */
    const setFeaturedImage = (index) => {
        const newImages = images.map((img, i) => ({
            ...img,
            isFeatured: i === index
        }));

        onImagesChange(newImages);
    };

    /**
     * Update image metadata
     */
    const updateImageMeta = (index, field, value) => {
        const newImages = [...images];
        newImages[index] = {
            ...newImages[index],
            [field]: value
        };

        onImagesChange(newImages);
    };

    /**
     * Open file dialog
     */
    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="space-y-4">
            {/* Upload Area */}
            <div
                className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive
                        ? 'border-highland-500 bg-highland-50'
                        : 'border-soil-100 hover:border-soil-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={IMAGE_UPLOAD_CONFIG.ALLOWED_TYPES.join(',')}
                    onChange={handleFileInput}
                    className="hidden"
                />

                <Upload className="mx-auto h-12 w-12 text-soil-400 mb-4" />

                <p className="text-sm text-soil-600 mb-2">
                    <button
                        type="button"
                        onClick={openFileDialog}
                        className="text-highland-500 hover:text-highland-600 font-medium"
                    >
                        Click to upload
                    </button>
                    {' '}or drag and drop
                </p>

                <p className="text-xs text-soil-400">
                    PNG, JPG, GIF, WEBP up to {IMAGE_UPLOAD_CONFIG.MAX_SIZE / (1024 * 1024)}MB
                </p>

                <p className="text-xs text-soil-400 mt-1">
                    Maximum {maxFiles} images • {images.length}/{maxFiles} uploaded
                </p>
            </div>

            {/* Errors */}
            {errors.length > 0 && (
                <div className="bg-murram-100/40 border border-murram-100 rounded-lg p-4">
                    <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-murram-600 mt-0.5 mr-3 flex-shrink-0" />
                        <div className="flex-1">
                            <h4 className="text-sm font-medium text-murram-600 mb-1">Upload Errors</h4>
                            <ul className="text-sm text-murram-600 space-y-1">
                                {errors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </div>
                        <button
                            onClick={() => setErrors([])}
                            className="text-murram-600 hover:text-murram-600"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            )}

            {/* Image Previews */}
            {images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {images.map((image, index) => (
                        <div
                            key={index}
                            className="relative group border rounded-lg overflow-hidden bg-soil-50"
                        >
                            {/* Image Preview */}
                            <div className="aspect-square relative">
                                <img
                                    src={image.previewUrl}
                                    alt={image.title || `Upload ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />

                                {/* Overlay */}
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center">
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity space-x-2">
                                        {/* Set as Featured */}
                                        <button
                                            type="button"
                                            onClick={() => setFeaturedImage(index)}
                                            className={`p-2 rounded-full ${
                                                image.isFeatured
                                                    ? 'bg-dawn-500 text-white'
                                                    : 'bg-white text-soil-600 hover:bg-soil-100'
                                            }`}
                                            title={image.isFeatured ? 'Featured Image' : 'Set as Featured'}
                                        >
                                            <Star className="h-4 w-4" fill={image.isFeatured ? 'currentColor' : 'none'} />
                                        </button>

                                        {/* Remove */}
                                        <button
                                            type="button"
                                            onClick={() => removeImage(index)}
                                            className="p-2 bg-murram-500 text-white rounded-full hover:bg-murram-600"
                                            title="Remove Image"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* Featured Badge */}
                                {image.isFeatured && (
                                    <div className="absolute top-2 left-2 bg-dawn-500 text-white text-xs font-medium px-2 py-1 rounded-full flex items-center shadow-md">
                                        <Star className="h-3 w-3 mr-1" fill="currentColor" />
                                        Featured
                                    </div>
                                )}
                            </div>

                            {/* Image Metadata */}
                            <div className="p-2 space-y-2">
                                <input
                                    type="text"
                                    placeholder="Image title (optional)"
                                    value={image.title}
                                    onChange={(e) => updateImageMeta(index, 'title', e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-soil-100 rounded focus:outline-none focus:ring-1 focus:ring-highland-500"
                                />

                                <textarea
                                    placeholder="Description (optional)"
                                    value={image.description}
                                    onChange={(e) => updateImageMeta(index, 'description', e.target.value)}
                                    rows="2"
                                    className="w-full px-2 py-1 text-xs border border-soil-100 rounded focus:outline-none focus:ring-1 focus:ring-highland-500 resize-none"
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty State */}
            {images.length === 0 && (
                <div className="text-center py-8 text-soil-400">
                    <ImageIcon className="mx-auto h-12 w-12 mb-3 text-soil-400" />
                    <p className="text-sm">No images uploaded yet</p>
                    <p className="text-xs mt-1">Add photos to showcase your property</p>
                </div>
            )}

            {/* Instructions */}
            {images.length > 0 && (
                <div className="bg-highland-50 border border-highland-100 rounded-lg p-3">
                    <p className="text-xs text-highland-600">
                        <Star className="inline h-3 w-3 mr-1" />
                        <strong>Tip:</strong> Click the star icon to set a featured image.
                        The featured image will be displayed as the main photo for your property listing.
                    </p>
                </div>
            )}
        </div>
    );
};

export default ImageUploader;
